<template>
    <transition name="dialog-fade">
        <div
            v-if="modelValue"
            class="mobile-dialog-overlay d-flex align-center justify-center position-fixed"
            :class="{ 'show-footer': showFooter }"
            @click.self="handleBackdropClick"
        >
            <transition name="dialog-scale">
                <div
                    v-if="modelValue"
                    class="mobile-dialog-container bg-section d-flex flex-column"
                    @click.stop
                >
                    <div
                        class="mobile-dialog-header d-flex align-center justify-space-between bg-section position-relative"
                    >
                        <h2
                            class="mobile-dialog-title text-fontLight font-weight-bold"
                        >
                            <slot name="title">
                                {{ title }}
                            </slot>
                        </h2>
                        <KIconButton
                            v-if="showCloseButton"
                            icon="sym_r_close"
                            :iconSize="24"
                            iconColor="icon"
                            @click="handleCloseIconClick"
                        />
                    </div>

                    <div
                        class="mobile-dialog-body overflow-y-auto prospus-scrollbar flex-grow-1 bg-section"
                        :class="bodyClass"
                    >
                        <slot name="content">
                            <div v-if="htmlContent" v-html="htmlContent" />
                            <div v-else-if="subtitle" class="text-fontLight">
                                {{ subtitle }}
                            </div>
                        </slot>
                    </div>
                    <div
                        v-if="showFooter"
                        class="mobile-dialog-footer d-flex align-center justify-end"
                    >
                        <slot name="footer"></slot>
                    </div>
                </div>
            </transition>
        </div>
    </transition>
</template>

<script setup>
import KIconButton from "Admin/js/Components/Common/K/KIconButton.vue";

const props = defineProps({
    modelValue: {
        type: Boolean,
        required: true,
        default: false,
    },
    title: {
        type: String,
        default: "",
    },
    subtitle: {
        type: String,
        default: "",
    },
    htmlContent: {
        type: String,
        default: "",
    },
    showCloseButton: {
        type: Boolean,
        default: true,
    },
    closeOnBackdrop: {
        type: Boolean,
        default: true,
    },
    showFooter: {
        type: Boolean,
        default: false,
    },
    bodyClass: {
        type: String,
        default: "",
    },
});

const emit = defineEmits(["update:modelValue", "onClose", "onCloseIconClick"]);

function handleClose() {
    emit("update:modelValue", false);
    emit("onClose");
}

function handleBackdropClick() {
    if (props.closeOnBackdrop) {
        handleClose();
    }
}

function handleCloseIconClick() {
    emit("onCloseIconClick");
    handleClose();
}
</script>

<style lang="scss" scoped>
@import "../../../../sass/_theme-variables.sass";

.mobile-dialog-overlay {
    inset: 0;
    z-index: 101;
    padding: 1rem;
    background-color: rgba(0, 0, 0, 0.2);
    backdrop-filter: blur(4px);
    &.show-footer {
        z-index: 100;
    }
}

.mobile-dialog-container {
    width: 100%;
    max-width: 24rem;
    max-height: 65dvh;
    overflow: hidden;
    border-radius: 12px;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

.mobile-dialog-header {
    height: 4rem;
    border-bottom: 1px solid rgb(var(--v-theme-sectionBorder));
    flex: none;
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
    padding-left: 1rem;
    padding-right: 1rem;
}

.mobile-dialog-footer {
    min-height: 4rem;
    border-top: 1px solid rgb(var(--v-theme-sectionBorder));
    flex: none;
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
    padding-left: 1rem;
    padding-right: 1rem;
}

.mobile-dialog-title {
    font-size: 18px;
    flex-grow: 1;
    padding-right: 0.5rem;
    word-wrap: break-word;
    overflow-wrap: break-word;
}

.mobile-dialog-body {
    font-size: 14px;
    line-height: 1.625;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 1rem;
    -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */
}

.dialog-fade-enter-active,
.dialog-fade-leave-active {
    transition: opacity 200ms ease;
}

.dialog-fade-enter-from,
.dialog-fade-leave-to {
    opacity: 0;
}

.dialog-scale-enter-active {
    animation: dialog-scale-in 200ms ease-out;
}

.dialog-scale-leave-active {
    animation: dialog-scale-out 150ms ease-in;
}

@keyframes dialog-scale-in {
    from {
        opacity: 0;
        transform: scale(0.95);
    }
    to {
        opacity: 1;
        transform: scale(1);
    }
}

@keyframes dialog-scale-out {
    from {
        opacity: 1;
        transform: scale(1);
    }
    to {
        opacity: 0;
        transform: scale(0.95);
    }
}
</style>
